import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FirebaseError } from 'firebase/app';

import {
  autoRegisterMember,
  fetchMemberCaptchaImage,
  loginMemberByFirebase,
  loginMemberByPassword,
  registerMemberByPassword,
  resetMemberPassword,
  sendMemberEmailCode,
  type MemberAutoRegisterResult,
  type MemberLoginResult,
} from '@/api/member';
import {
  consumeFirebaseRedirectLogin,
  hasPendingFirebaseRedirect,
  signInWithGoogle,
  signInWithTwitter,
} from '@/utils/firebaseAuth';
import { ensureTelegramLoginSDK, signInWithTelegram, loginWithTelegramIDToken } from '@/utils/telegramAuth';
import { buildSessionFromLogin, clearReferralCode, consumeMemberLoginReturn, readReferralCode, refreshCurrentMember, saveMemberSession } from '@/utils/memberSession';
import { copyTextToClipboard, downloadTextFile } from '@/utils/nativeApp';

const lastLoginAccountKey = 'storybox_last_login_account';
const lastLoginEmailKey = 'storybox_last_login_email';
type Screen = 'login' | 'register' | 'forgot';
type LoginType = 'account' | 'email';
type RegisterType = 'account' | 'email';
type FirebaseLoginProvider = 'google' | 'twitter';
interface RegistrationSuccess {
  account: string;
  rewardPoints: number;
}

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export default function CinemaLoginPage() {
  const { t } = useTranslation('cinema');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [screen, setScreen] = useState<Screen>('login');
  const [loginType, setLoginType] = useState<LoginType>('account');
  const [registerType, setRegisterType] = useState<RegisterType>('account');
  const [username, setUsername] = useState(() => window.localStorage.getItem(lastLoginAccountKey) || '');
  const [email, setEmail] = useState(() => window.localStorage.getItem(lastLoginEmailKey) || '');
  const [password, setPassword] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [captchaValue, setCaptchaValue] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [agreeTerms, setAgreeTerms] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [autoCredentials, setAutoCredentials] = useState<MemberAutoRegisterResult | null>(null);
  const [registrationSuccess, setRegistrationSuccess] = useState<RegistrationSuccess | null>(null);
  const [copyNotice, setCopyNotice] = useState('');
  const copyNoticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shareCode = useMemo(() => readReferralCode(), []);

  const loadCaptcha = useCallback(async () => {
    try {
      const result = await fetchMemberCaptchaImage();
      setCaptchaId(result.captchaId || '');
      setCaptchaImage(result.image || '');
      setCaptchaValue('');
    } catch (error) {
      setCaptchaId('');
      setCaptchaImage('');
      setMessage(error instanceof Error ? error.message : '验证码加载失败');
    }
  }, []);

  useEffect(() => { void loadCaptcha(); }, [loadCaptcha]);
  useEffect(() => {
    const authError = searchParams.get('auth_error');
    if (authError) setMessage(authError);
  }, [searchParams]);
  useEffect(() => {
    void ensureTelegramLoginSDK().catch(() => {
      // The button remains available and will surface the SDK error if it persists.
    });
  }, []);
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = window.setTimeout(() => setCountdown((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [countdown]);
  useEffect(() => () => {
    if (copyNoticeTimerRef.current) clearTimeout(copyNoticeTimerRef.current);
  }, []);

  const switchScreen = (next: Screen) => {
    setScreen(next);
    setMessage('');
    setPassword('');
    setEmailCode('');
    void loadCaptcha();
  };

  const persistLogin = useCallback(async (result: MemberLoginResult) => {
    if (!result?.user?.id || !result.accessToken || !result.refreshToken) {
      throw new Error('登录响应不完整，请重新登录');
    }
    saveMemberSession(buildSessionFromLogin(result));
    await refreshCurrentMember().catch(() => undefined);
    if (result.isNewUser) clearReferralCode();
  }, []);

  const finishLogin = useCallback(async (result: MemberLoginResult) => {
    await persistLogin(result);
    navigate(consumeMemberLoginReturn(), { replace: true });
  }, [navigate, persistLogin]);

  useEffect(() => {
    if (!hasPendingFirebaseRedirect()) return;
    let cancelled = false;
    setIsLoading(true);
    setMessage('正在完成快捷登录...');
    void consumeFirebaseRedirectLogin()
      .then(async (user) => {
        if (!user || cancelled) return;
        const provider = user.provider === 'google' ? 'google' : 'twitter';
        await finishLogin(await loginMemberByFirebase({ provider, id_token: user.idToken, shareCode }));
      })
      .catch((error) => {
        if (!cancelled) setMessage(error instanceof Error ? error.message : '快捷登录失败');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [finishLogin, shareCode]);

  const continueAfterRegistration = () => {
    setAutoCredentials(null);
    setRegistrationSuccess(null);
    navigate(consumeMemberLoginReturn(), { replace: true });
  };

  const requireCaptcha = () => {
    if (!captchaId || !/^\d{4}$/.test(captchaValue)) {
      setMessage('请输入4位数字验证码');
      if (!captchaId) void loadCaptcha();
      return false;
    }
    return true;
  };

  const handlePasswordLogin = async () => {
    const identifier = loginType === 'email' ? email.trim() : username.trim();
    if (!identifier) return setMessage(loginType === 'email' ? '请输入邮箱' : '请输入账号');
    if (loginType === 'email' && !isValidEmail(identifier)) return setMessage('邮箱格式不正确');
    if (!password) return setMessage('请输入密码');
    if (!requireCaptcha()) return;
    setIsLoading(true);
    setMessage('');
    try {
      const result = await loginMemberByPassword({ username: identifier, password, captchaId, captchaValue: captchaValue.trim() });
      window.localStorage.setItem(loginType === 'email' ? lastLoginEmailKey : lastLoginAccountKey, identifier);
      await finishLogin(result);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '登录失败');
      void loadCaptcha();
    } finally {
      setIsLoading(false);
    }
  };

  const handleAutoRegister = async () => {
    if (screen === 'register' && !agreeTerms) return setMessage('请先同意用户协议和隐私政策');
    setIsLoading(true);
    setMessage('');
    try {
      const result = await autoRegisterMember(shareCode);
      await persistLogin(result);
      setAutoCredentials(result);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '注册失败');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!agreeTerms) return setMessage('请先同意用户协议和隐私政策');
    if (!password) return setMessage('请输入密码');
    if (password.length < 6) return setMessage('密码至少需要 6 位');
    if (registerType === 'account' && !username.trim()) return setMessage('请输入账号');
    if (registerType === 'email' && !email.trim()) return setMessage('请输入邮箱');
    if (registerType === 'email' && !isValidEmail(email)) return setMessage('邮箱格式不正确');
    if (registerType === 'email' && !emailCode.trim()) return setMessage('请输入邮箱验证码');
    if (registerType === 'account' && !requireCaptcha()) return;
    setIsLoading(true);
    setMessage('');
    try {
      const result = await registerMemberByPassword({
        registerType,
        username: registerType === 'account' ? username.trim() : undefined,
        email: email.trim() || undefined,
        code: registerType === 'email' ? emailCode.trim() : undefined,
        password,
        shareCode,
        captchaId: registerType === 'account' ? captchaId : '',
        captchaValue: registerType === 'account' ? captchaValue.trim() : '',
      });
      window.localStorage.setItem(registerType === 'email' ? lastLoginEmailKey : lastLoginAccountKey, registerType === 'email' ? email.trim() : username.trim());
      await persistLogin(result);
      setRegistrationSuccess({
        account: result.user.userName || (registerType === 'email' ? email.trim() : username.trim()),
        rewardPoints: Number(result.rewardPoints) || 0,
      });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '注册失败');
      void loadCaptcha();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendResetCode = async () => {
    if (countdown > 0 || isLoading) return;
    if (!isValidEmail(email)) return setMessage('邮箱格式不正确');
    setIsLoading(true);
    setMessage('');
    try {
      await sendMemberEmailCode(email.trim(), 'password_reset');
      setCountdown(60);
      setMessage('验证码已发送到邮箱');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '发送失败');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendRegistrationCode = async () => {
    if (countdown > 0 || isLoading) return;
    if (!isValidEmail(email)) return setMessage('邮箱格式不正确');
    setIsLoading(true);
    setMessage('');
    try {
      await sendMemberEmailCode(email.trim(), 'registration');
      setCountdown(60);
      setMessage('验证码已发送到邮箱');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '发送失败');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!email.trim() || !emailCode.trim() || !password || !requireCaptcha()) return;
    setIsLoading(true);
    setMessage('');
    try {
      await resetMemberPassword({ username: username.trim() || undefined, email: email.trim(), code: emailCode.trim(), password, captchaId, captchaValue: captchaValue.trim() });
      setMessage('密码已重置，请使用新密码登录');
      window.setTimeout(() => switchScreen('login'), 800);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '重置密码失败');
      void loadCaptcha();
    } finally {
      setIsLoading(false);
    }
  };

  const handleFirebaseLogin = async (provider: FirebaseLoginProvider) => {
    if (isLoading) return;
    setIsLoading(true); setMessage('');
    try {
      const user = provider === 'google' ? await signInWithGoogle() : await signInWithTwitter();
      if (!user) return;
      await finishLogin(await loginMemberByFirebase({ provider, id_token: user.idToken, shareCode }));
    } catch (error) {
      if (error instanceof FirebaseError) {
        if (error.code === 'auth/cancelled-popup-request') return;
        if (error.code === 'auth/popup-closed-by-user') {
          setMessage('已取消登录');
          return;
        }
        if (error.code === 'auth/popup-blocked') {
          setMessage('登录弹窗被浏览器拦截，请允许弹窗后重试');
          return;
        }
      }
      setMessage(error instanceof Error ? error.message : provider === 'google' ? 'Google 登录失败' : 'X 登录失败');
    } finally { setIsLoading(false); }
  };

  const handleTelegramLogin = async () => {
    if (isLoading) return;
    setIsLoading(true); setMessage('');
    try {
      const telegram = await signInWithTelegram(shareCode);
      if (telegram.error) throw new Error(telegram.error);
      if (!telegram.id_token) throw new Error('Telegram 未返回登录凭证');
      await finishLogin(await loginWithTelegramIDToken(telegram.id_token, shareCode) as MemberLoginResult);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Telegram 登录失败');
    } finally { setIsLoading(false); }
  };

  const downloadCredentials = () => {
    if (!autoCredentials) return;
    const text = `黄果TV账号信息\r\n\r\n账号：${autoCredentials.account}\r\n密码：${autoCredentials.password}\r\n\r\n请妥善保存，丢失后可能无法找回当前账号。\r\n`;
    downloadTextFile(`mfantasy-account-${autoCredentials.account}.txt`, text);
  };

  const copyCredentials = async () => {
    if (!autoCredentials) return;
    const copied = await copyTextToClipboard(`账号：${autoCredentials.account}\n密码：${autoCredentials.password}`);
    setCopyNotice(copied ? '账号密码已复制' : '复制失败，请手动复制');
    if (copyNoticeTimerRef.current) clearTimeout(copyNoticeTimerRef.current);
    copyNoticeTimerRef.current = setTimeout(() => setCopyNotice(''), 2000);
  };

  const inputClass = 'h-10 md:h-12 w-full rounded-xl bg-background-900/60 border border-background-800/80 px-3 md:px-4 text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-[#F19744]/50';
  const primaryButtonClass = 'w-full h-11 md:h-12 rounded-xl text-sm font-semibold text-background-950 bg-[#F19744] hover:bg-[#F19744]/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer';

  return <div className="min-h-screen flex items-start justify-center bg-background-950 px-6 pb-8 pt-20 md:items-center md:px-4 md:py-8"><div className="w-full max-w-[350px] md:max-w-sm">
    <button onClick={() => screen === 'register' ? switchScreen('login') : navigate(-1)} className="hidden md:flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-300 transition-colors mb-8 cursor-pointer whitespace-nowrap"><i className="ri-arrow-left-line text-lg"></i>{t('cinema_back')}</button>
    <div className="text-center mb-5 md:mb-7"><h1 className="text-xl font-bold font-heading text-foreground-50">{screen === 'login' ? t('cinema_welcomeBack') : screen === 'register' ? '注册账号' : '找回密码'}</h1><p className={`hidden md:block text-sm text-foreground-500 mt-2`}>{screen === 'login' ? '使用账号或邮箱密码登录' : screen === 'register' ? '创建并保存你的登录账号' : '通过绑定邮箱重置密码'}</p></div>

    {screen === 'login' && <><TabBar value={loginType} options={[['account', '账号登录'], ['email', '邮箱登录']]} onChange={(value) => { setLoginType(value as LoginType); setMessage(''); }} /><div className="mb-3"><button type="button" onClick={() => switchScreen('register')} className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-[#F19744]/70 bg-[#F19744]/10 text-sm font-semibold text-[#F19744] transition-colors hover:bg-[#F19744]/20 md:h-12"><i className="ri-user-add-line text-base" />立即注册账号</button></div></>}
    {screen === 'register' && <><button type="button" onClick={() => void handleAutoRegister()} disabled={!agreeTerms || isLoading} className={`${primaryButtonClass} mb-4 flex items-center justify-center gap-2`}><i className="ri-sparkling-line"></i>{isLoading ? '注册中...' : '一键自动注册'}</button><TabBar value={registerType} options={[['account', '账号注册'], ['email', '邮箱注册']]} onChange={(value) => { setRegisterType(value as RegisterType); setMessage(''); setEmailCode(''); }} /></>}

    <div className="space-y-2 md:space-y-3">
      {screen === 'login' && <><input value={loginType === 'email' ? email : username} onChange={(event) => loginType === 'email' ? setEmail(event.target.value) : setUsername(event.target.value)} type={loginType === 'email' ? 'email' : 'text'} placeholder={loginType === 'email' ? '请输入邮箱' : '请输入账号'} className={inputClass} /><input value={password} onChange={(event) => setPassword(event.target.value)} type="password" placeholder="请输入密码" className={inputClass} /><CaptchaRow value={captchaValue} image={captchaImage} onChange={setCaptchaValue} onRefresh={() => void loadCaptcha()} /><button type="button" onClick={() => void handlePasswordLogin()} disabled={isLoading} className={primaryButtonClass}>{isLoading ? '登录中...' : t('cinema_loginNow')}</button><div className="flex justify-between px-1 text-xs"><button onClick={() => switchScreen('register')} className="text-[#F19744] cursor-pointer">注册账号</button><button onClick={() => switchScreen('forgot')} className="text-foreground-400 cursor-pointer">忘记密码？</button></div></>}
      {screen === 'register' && <>{registerType === 'account' && <input value={username} onChange={(event) => setUsername(event.target.value)} placeholder="请输入账号" className={inputClass} />}{registerType === 'email' && <><input value={email} onChange={(event) => setEmail(event.target.value)} type="email" placeholder="请输入邮箱" className={inputClass} /><div className="grid grid-cols-[1fr_auto] gap-2"><input value={emailCode} onChange={(event) => setEmailCode(event.target.value.replace(/\D/g, ''))} inputMode="numeric" placeholder="邮箱验证码" className={inputClass} /><button type="button" onClick={() => void handleSendRegistrationCode()} disabled={countdown > 0 || isLoading} className="h-12 rounded-xl bg-background-800 px-3 text-xs font-semibold text-foreground-200 disabled:opacity-40 cursor-pointer">{countdown > 0 ? `${countdown}s` : '获取验证码'}</button></div></>}<input value={password} onChange={(event) => setPassword(event.target.value)} type="password" placeholder="请设置密码（至少 6 位）" className={inputClass} />{registerType === 'account' && <CaptchaRow value={captchaValue} image={captchaImage} onChange={setCaptchaValue} onRefresh={() => void loadCaptcha()} />}<button type="button" onClick={() => void handleRegister()} disabled={!agreeTerms || isLoading} className={primaryButtonClass}>{isLoading ? '注册中...' : '注册并登录'}</button></>}
      {screen === 'forgot' && <><input value={username} onChange={(event) => setUsername(event.target.value)} placeholder="账号（同邮箱有多个账号时必填）" className={inputClass} /><input value={email} onChange={(event) => setEmail(event.target.value)} type="email" placeholder="请输入绑定邮箱" className={inputClass} /><div className="grid grid-cols-[1fr_auto] gap-2"><input value={emailCode} onChange={(event) => setEmailCode(event.target.value.replace(/\D/g, ''))} inputMode="numeric" placeholder="邮箱验证码" className={inputClass} /><button type="button" onClick={() => void handleSendResetCode()} disabled={countdown > 0 || isLoading} className="h-12 rounded-xl bg-background-800 px-3 text-xs font-semibold text-foreground-200 disabled:opacity-40 cursor-pointer">{countdown > 0 ? `${countdown}s` : '获取验证码'}</button></div><input value={password} onChange={(event) => setPassword(event.target.value)} type="password" placeholder="设置新密码（至少 6 位）" className={inputClass} /><CaptchaRow value={captchaValue} image={captchaImage} onChange={setCaptchaValue} onRefresh={() => void loadCaptcha()} /><button type="button" onClick={() => void handleResetPassword()} disabled={isLoading} className={primaryButtonClass}>{isLoading ? '处理中...' : '重置密码'}</button><div className="text-center"><button onClick={() => switchScreen('login')} className="text-xs text-[#F19744] cursor-pointer">返回登录</button></div></>}
      {message && <p className="rounded-lg bg-background-900/70 px-3 py-2 text-center text-xs text-accent-400">{message}</p>}
      {screen === 'register' && <TermsCheckbox checked={agreeTerms} onChange={() => setAgreeTerms((value) => !value)} t={t} />}
    </div>

    {screen !== 'forgot' && <><div className="flex items-center gap-3 my-4 md:my-6"><div className="flex-1 h-px bg-background-800" /><span className="text-xs text-foreground-600 whitespace-nowrap">快捷登录</span><div className="flex-1 h-px bg-background-800" /></div>{screen === 'login' && <button type="button" onClick={() => void handleAutoRegister()} disabled={isLoading} className="mb-4 flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-[#F19744]/80 bg-background-800/90 text-sm font-semibold text-[#F19744] transition-colors hover:bg-[#F19744]/20 focus-visible:bg-[#F19744]/20 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-40 md:h-12"><i className="ri-flashlight-line text-base" />{isLoading ? '注册中...' : '一键注册登录'}</button>}<div className="flex items-center justify-center gap-4"><button onClick={() => void handleFirebaseLogin('google')} disabled={isLoading} aria-label="使用 Google 登录" title="使用 Google 登录" className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-900/60 border border-background-800/80 text-foreground-400 hover:text-foreground-200 transition-all cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"><i className="ri-google-line text-lg"></i></button><button onClick={() => void handleFirebaseLogin('twitter')} disabled={isLoading} aria-label="使用 X 登录" title="使用 X 登录" className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-900/60 border border-background-800/80 text-foreground-400 hover:text-foreground-200 transition-all cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"><i className="ri-twitter-x-line text-lg"></i></button><button onClick={() => void handleTelegramLogin()} disabled={isLoading} aria-label="使用 Telegram 登录" title="使用 Telegram 登录" className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-900/60 border border-background-800/80 text-foreground-400 hover:text-foreground-200 transition-all cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"><i className="ri-telegram-line text-lg"></i></button></div></>}
  </div>
  {autoCredentials && <div className="cinema-modal-safe fixed inset-0 z-[60] flex items-center justify-center px-4 pt-4 sm:p-4"><div className="absolute inset-0 bg-black/80 backdrop-blur-sm" /><div className="relative w-full max-w-sm rounded-2xl border border-background-800 bg-background-900 p-5 shadow-2xl"><h2 className="text-center text-lg font-bold text-foreground-50">账号已生成</h2><div className="mt-4 rounded-xl bg-background-800/70 p-4 text-sm text-foreground-200"><div>账号：{autoCredentials.account}</div><div className="mt-2">密码：{autoCredentials.password}</div></div><div className="mt-5 grid grid-cols-2 gap-3"><button onClick={() => void copyCredentials()} className="rounded-xl bg-background-800 px-3 py-3 text-sm font-semibold text-foreground-200">一键复制</button><button onClick={downloadCredentials} className="rounded-xl bg-background-800 px-3 py-3 text-sm font-semibold text-foreground-200">下载保存</button></div>{copyNotice && <div role="status" aria-live="polite" className="mt-3 flex items-center justify-center gap-2 rounded-xl bg-[#F19744]/15 px-3 py-2 text-sm font-medium text-[#F19744]"><i className={copyNotice.startsWith('复制失败') ? 'ri-error-warning-line' : 'ri-checkbox-circle-line'} />{copyNotice}</div>}<button onClick={continueAfterRegistration} className="mt-3 w-full rounded-xl bg-[#F19744] px-3 py-3 text-sm font-bold text-background-950">继续访问</button></div></div>}
  {registrationSuccess && <div className="cinema-modal-safe fixed inset-0 z-[60] flex items-center justify-center px-4 pt-4 sm:p-4"><div className="absolute inset-0 bg-black/80 backdrop-blur-sm" /><div className="relative w-full max-w-xs rounded-2xl border border-background-800 bg-background-900 p-5 text-center shadow-2xl"><span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#F19744]/15 text-[#F19744]"><i className="ri-check-line text-2xl" /></span><h2 className="mt-3 text-lg font-bold text-foreground-50">注册成功</h2><p className="mt-2 text-sm text-foreground-400">账号 {registrationSuccess.account} 已自动登录</p>{registrationSuccess.rewardPoints > 0 && <p className="mt-2 text-sm font-semibold text-[#F19744]">已赠送 {registrationSuccess.rewardPoints} 金币</p>}<button onClick={continueAfterRegistration} className="mt-5 w-full rounded-xl bg-[#F19744] px-3 py-3 text-sm font-bold text-background-950">继续访问</button></div></div>}
  </div>;
}

function TabBar({ value, options, onChange }: { value: string; options: readonly (readonly [string, string])[]; onChange: (value: string) => void }) {
  return <div className="flex bg-background-900/60 rounded-full p-1 mb-3 md:mb-5">{options.map(([key, label]) => <button key={key} onClick={() => onChange(key)} className={`flex-1 py-1.5 md:py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${value === key ? 'bg-[#F19744] text-background-950' : 'text-foreground-500 hover:text-foreground-300'}`}>{label}</button>)}</div>;
}

function CaptchaRow({ value, image, onChange, onRefresh }: { value: string; image: string; onChange: (value: string) => void; onRefresh: () => void }) {
  return <div className="flex items-stretch gap-2"><input value={value} onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(0, 4))} inputMode="numeric" maxLength={4} autoComplete="one-time-code" placeholder="请输入4位数字验证码" className="h-10 md:h-12 min-w-0 flex-1 rounded-xl bg-background-900/60 border border-background-800/80 px-3 md:px-4 text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-[#F19744]/50" /><button type="button" onClick={onRefresh} className="h-10 md:h-12 w-[112px] md:w-[120px] flex-shrink-0 overflow-hidden rounded-xl border border-background-800/80 bg-background-900/60 cursor-pointer">{image ? <img src={image} alt="刷新图形验证码" className="h-full w-full object-cover" /> : <span className="text-xs text-foreground-500">刷新验证码</span>}</button></div>;
}

function TermsCheckbox({ checked, onChange, t }: { checked: boolean; onChange: () => void; t: (key: string) => string }) {
  return <div className="flex items-start gap-2"><button type="button" onClick={onChange} className={`mt-0.5 w-4 h-4 flex items-center justify-center rounded border transition-all flex-shrink-0 cursor-pointer ${checked ? 'bg-[#F19744] border-[#F19744] text-background-950' : 'border-foreground-600 text-transparent'}`}><i className="ri-check-line text-[10px]"></i></button><span className="text-[11px] text-foreground-500 leading-relaxed">{t('cinema_agreeTerms')} <span className="text-[#F19744]">{t('cinema_userAgreement')}</span> &amp; <span className="text-[#F19744]">{t('cinema_privacyPolicy')}</span></span></div>;
}
